/**
 * Cart Drawer - AJAX Cart with Slide-out Drawer
 * Intercepts Add to Cart and opens drawer instead of navigating
 */
(function() {
  'use strict';

  const PAGE_SCROLL_LOCK_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
    'no-scroll',
    'scroll-lock',
    'scroll-locked',
    'drawer-open',
    'cart-open',
    'cart-drawer-open',
    'js-drawer-open',
    'modal-open'
  ];
  const PAGE_SCROLL_LOCK_STYLES = [
    'overflow',
    'overflow-x',
    'overflow-y',
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'width',
    'height',
    'touch-action'
  ];

  console.log('Cart Drawer: Script loaded');

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('Cart Drawer: Initializing...');

    // CRITICAL: Intercept at the document level with capture phase
    document.addEventListener('submit', handleFormSubmit, true);
    document.addEventListener('click', handleButtonClick, true);

    // Setup close listeners
    document.addEventListener('click', handleCloseClick);
    document.addEventListener('keydown', handleEscape);

    // Setup upsell add button listeners
    document.addEventListener('click', handleUpsellAdd);

    // Initial update
    updateDrawerContents();

    console.log('Cart Drawer: Ready');
  }

  // Remove any page scroll-lock classes/styles a previous modal/drawer may have
  // left on <html>/<body>. IMPORTANT: only touch attributes that are actually
  // present. classList.remove()/style.removeProperty() otherwise re-serialize
  // and re-write the attribute even when nothing changes — which, if anything
  // were observing these attributes, would feed a mutation loop. Keeping this
  // idempotent means a call with nothing to clean performs zero DOM writes.
  function releasePageScrollLocks() {
    [document.documentElement, document.body].forEach(el => {
      if (!el) return;

      PAGE_SCROLL_LOCK_CLASSES.forEach(className => {
        if (el.classList.contains(className)) el.classList.remove(className);
      });

      PAGE_SCROLL_LOCK_STYLES.forEach(property => {
        if (el.style.getPropertyValue(property)) el.style.removeProperty(property);
      });
    });
  }

  // Run the cleanup exactly once when the drawer opens. No MutationObserver:
  // a continuous observer whose callback strips class/style attributes re-fires
  // on its own writes → infinite loop → frozen page at 100% CPU.
  function startPageScrollLockGuard() {
    releasePageScrollLocks();
  }

  // And exactly once when it closes.
  function stopPageScrollLockGuard() {
    releasePageScrollLocks();
  }

  function handleFormSubmit(e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    // Check if this is an add to cart form
    const action = form.getAttribute('action') || '';
    const isAddToCart = form.classList.contains('jaxxon-product-form') ||
                        form.classList.contains('product-form') ||
                        action.includes('/cart/add') ||
                        action.includes('/cart');

    // Exclude non-cart forms
    if (action.includes('contact') || action.includes('customer') || action.includes('newsletter')) {
      return;
    }

    if (isAddToCart) {
      console.log('Cart Drawer: Form submit intercepted!', form);

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      addToCartAjax(form);
      return false;
    }
  }

  function handleButtonClick(e) {
    // Add to Cart button selectors
    const button = e.target.closest('button.jaxxon-add-to-cart, button[name="add"], .jaxxon-add-to-cart, .product-form__submit, #StickyATCBtn, [data-add-to-cart]');

    if (button && !button.disabled) {
      const form = button.closest('form');

      if (form) {
        const action = form.getAttribute('action') || '';
        if (action.includes('/cart') || form.classList.contains('jaxxon-product-form') || form.classList.contains('product-form')) {
          console.log('Cart Drawer: Button click intercepted!', button);

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          addToCartAjax(form);
          return false;
        }
      }
    }

    // Cart icon click opens drawer
    const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"], .header__icon--cart, [data-cart-icon], #cart-icon-bubble');
    if (cartLink && !cartLink.closest('.cart-drawer')) {
      console.log('Cart Drawer: Cart icon click intercepted');
      e.preventDefault();
      e.stopPropagation();
      updateDrawerContents();
      openDrawer();
      return false;
    }
  }

  function addToCartAjax(form) {
    const formData = new FormData(form);
    const button = form.querySelector('button[name="add"], .jaxxon-add-to-cart, .product-form__submit, [type="submit"]');
    const originalHTML = button ? button.innerHTML : '';

    // Disable button during request
    if (button) {
      button.disabled = true;
      button.innerHTML = button.innerHTML.replace(/Add to Cart|Quick Add/gi, 'Adding...');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => { throw new Error(err.description || 'Add to cart failed'); });
      }
      return response.json();
    })
    .then(item => {
      console.log('Cart Drawer: Item added successfully', item);

      // Snapchat Pixel: ADD_CART event
      if (typeof snaptr === 'function') {
        snaptr('track', 'ADD_CART', {
          'price': (item.price / 100).toFixed(2),
          'currency': window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD',
          'item_ids': [String(item.product_id)],
          'item_category': item.product_type || '',
          'number_items': item.quantity || 1
        });
      }

      updateDrawerContents();
      openDrawer();
    })
    .catch(error => {
      console.error('Cart Drawer: Error', error);
      alert(error.message || 'Could not add to cart. Please try again.');
    })
    .finally(() => {
      // Re-enable button
      if (button) {
        button.disabled = false;
        button.innerHTML = originalHTML;
      }
    });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function updateDrawerContents() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('Cart Drawer: Cart data', cart);

        // Update count in drawer header
        const countEl = document.querySelector('.cart-drawer-count');
        if (countEl) countEl.textContent = 'Your Bag (' + cart.item_count + ')';

        // Update header cart count bubble
        document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });

        // Update subtotal (cart.total_price already reflects automatic discounts)
        const subtotalEl = document.querySelector('.cart-drawer-subtotal');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

        // Show REAL bundle savings pulled from cart AJAX data (not hardcoded).
        // cart.total_discount is the actual amount Shopify discounted, so this
        // always matches the live automatic discount even if its value changes.
        const savingsRow = document.querySelector('.cart-drawer-savings-row');
        const savingsEl = document.querySelector('.cart-drawer-savings');
        if (savingsRow && savingsEl) {
          if (cart.total_discount && cart.total_discount > 0) {
            savingsEl.textContent = '-' + formatMoney(cart.total_discount);
            savingsRow.style.display = 'flex';
          } else {
            savingsRow.style.display = 'none';
          }
        }

        // Render items
        renderItems(cart);

        // Toggle empty state
        const emptyEl = document.querySelector('.cart-drawer-empty');
        const contentEl = document.querySelector('.cart-drawer-content');
        const footerEl = document.querySelector('.cart-drawer-footer');

        if (cart.item_count === 0) {
          if (emptyEl) emptyEl.style.display = 'block';
          if (contentEl) contentEl.style.display = 'none';
          if (footerEl) footerEl.style.display = 'none';
        } else {
          if (emptyEl) emptyEl.style.display = 'none';
          if (contentEl) contentEl.style.display = 'block';
          if (footerEl) footerEl.style.display = 'block';
        }
      })
      .catch(err => {
        console.error('Cart Drawer: Error fetching cart', err);
      });
  }

  function renderItems(cart) {
    const container = document.querySelector('.cart-drawer-items');
    if (!container) return;

    if (cart.items.length === 0) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = cart.items.map((item, index) => {
      const imgSrc = item.image ? item.image.replace(/(\.[^.]+)$/, '_140x$1') : '';
      const hasDiscount = item.original_line_price !== item.final_line_price;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

      return `
        <div class="cart-drawer-item" data-line="${index + 1}" data-key="${item.key}">
          <a href="${item.url}" class="cart-drawer-item-img">
            <img src="${imgSrc}" alt="${item.title}" width="70" height="70">
          </a>
          <div class="cart-drawer-item-info">
            <a href="${item.url}" class="cart-drawer-item-title">${item.product_title}</a>
            ${variantTitle ? `<p class="cart-drawer-item-variant">${variantTitle}</p>` : ''}
            <div class="cart-drawer-item-qty">
              <button type="button" class="qty-btn" data-action="minus" data-line="${index + 1}">−</button>
              <span class="qty-value">${item.quantity}</span>
              <button type="button" class="qty-btn" data-action="plus" data-line="${index + 1}">+</button>
            </div>
          </div>
          <div class="cart-drawer-item-price">
            ${hasDiscount ? `<span class="cart-drawer-item-compare">${formatMoney(item.original_line_price)}</span>` : ''}
            <span class="cart-drawer-item-final">${formatMoney(item.final_line_price)}</span>
          </div>
          <button type="button" class="cart-drawer-item-remove" data-line="${index + 1}" aria-label="Remove">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      `;
    }).join('');

    // Add event listeners for quantity and remove
    container.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', handleQuantity);
    });
    container.querySelectorAll('.cart-drawer-item-remove').forEach(btn => {
      btn.addEventListener('click', handleRemove);
    });
  }

  // --- Optimistic cart updates ------------------------------------------------
  // The DOM updates instantly on +/-/remove; the /cart/change request is sent in
  // the background (debounced per line) and the server response reconciles the
  // drawer. The server stays the source of truth, so cart/discount correctness
  // is unchanged — only the *perceived* speed improves.

  // line-item key -> pending debounce timer
  const pendingChanges = {};

  function parseMoneyCents(el) {
    if (!el) return 0;
    const n = parseFloat((el.textContent || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function bumpSubtotal(deltaCents) {
    const subtotalEl = document.querySelector('.cart-drawer-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(Math.max(0, parseMoneyCents(subtotalEl) + deltaCents));
  }

  function bumpCount(deltaQty) {
    const countEl = document.querySelector('.cart-drawer-count');
    if (countEl) {
      const m = countEl.textContent.match(/(\d+)/);
      const n = m ? parseInt(m[1], 10) : 0;
      countEl.textContent = 'Your Bag (' + Math.max(0, n + deltaQty) + ')';
    }
    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => {
      const n = parseInt(el.textContent, 10) || 0;
      const next = Math.max(0, n + deltaQty);
      el.textContent = next;
      const bubble = el.closest('.cart-count-bubble');
      if (bubble) bubble.style.display = next > 0 ? 'flex' : 'none';
    });
  }

  // Only re-render from the server once no other line still has a pending sync,
  // so a reconcile for one line never clobbers another line's optimistic state.
  function reconcileIfIdle() {
    if (Object.keys(pendingChanges).length === 0) updateDrawerContents();
  }

  // Debounced absolute-quantity sync. Sending the *latest* quantity (rather than
  // one request per click) prevents out-of-order responses from corrupting the
  // count during rapid tapping.
  function scheduleSync(key, qty) {
    if (pendingChanges[key]) clearTimeout(pendingChanges[key]);
    pendingChanges[key] = setTimeout(() => {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
      .then(r => r.json())
      .then(() => { delete pendingChanges[key]; reconcileIfIdle(); })
      .catch(err => {
        console.error('Cart Drawer: change error', err);
        delete pendingChanges[key];
        updateDrawerContents(); // hard reconcile on failure
      });
    }, 350);
  }

  function handleQuantity(e) {
    const btn = e.currentTarget;
    const item = btn.closest('.cart-drawer-item');
    if (!item) return;
    const key = item.dataset.key;
    const qtySpan = item.querySelector('.qty-value');
    const oldQty = parseInt(qtySpan.textContent, 10) || 1;
    const newQty = btn.dataset.action === 'plus' ? oldQty + 1 : oldQty - 1;

    if (newQty < 1) { optimisticRemove(item, key); return; }

    // Optimistic: quantity number
    qtySpan.textContent = newQty;

    // Optimistic: line price — scale the (already-discounted) price per unit.
    // Exact for %-based discounts; the server reconcile corrects any edge case.
    const finalEl = item.querySelector('.cart-drawer-item-final');
    const compareEl = item.querySelector('.cart-drawer-item-compare');
    const oldFinal = parseMoneyCents(finalEl);
    const newFinal = Math.round((oldFinal / oldQty) * newQty);
    if (finalEl) finalEl.textContent = formatMoney(newFinal);
    if (compareEl) {
      const oldCompare = parseMoneyCents(compareEl);
      compareEl.textContent = formatMoney(Math.round((oldCompare / oldQty) * newQty));
    }

    // Optimistic: subtotal + counts
    bumpSubtotal(newFinal - oldFinal);
    bumpCount(newQty - oldQty);

    scheduleSync(key, newQty);
  }

  function optimisticRemove(item, key) {
    const finalEl = item.querySelector('.cart-drawer-item-final');
    const qtySpan = item.querySelector('.qty-value');
    const lineCents = parseMoneyCents(finalEl);
    const qty = qtySpan ? (parseInt(qtySpan.textContent, 10) || 0) : 0;

    // Optimistic: subtract this line from subtotal + counts, then collapse it.
    bumpSubtotal(-lineCents);
    bumpCount(-qty);

    item.style.transition = 'opacity 0.15s ease';
    item.style.opacity = '0';
    item.style.pointerEvents = 'none';
    setTimeout(() => {
      item.remove();
      const container = document.querySelector('.cart-drawer-items');
      if (container && !container.querySelector('.cart-drawer-item')) {
        const emptyEl = document.querySelector('.cart-drawer-empty');
        const contentEl = document.querySelector('.cart-drawer-content');
        const footerEl = document.querySelector('.cart-drawer-footer');
        if (emptyEl) emptyEl.style.display = 'block';
        if (contentEl) contentEl.style.display = 'none';
        if (footerEl) footerEl.style.display = 'none';
      }
    }, 150);

    scheduleSync(key, 0);
  }

  function handleRemove(e) {
    const item = e.currentTarget.closest('.cart-drawer-item');
    if (!item) return;
    optimisticRemove(item, item.dataset.key);
  }

  // --- Upsell ("Complete the Look") variant helpers -------------------------
  // Multi-variant upsell products must NOT add the default/smallest variant
  // silently. The first "+" tap reveals inline size pills; a confirm tap adds
  // the exact selected variant. Single-variant products add immediately.

  function upsellGetVariants(card) {
    const el = card.querySelector('.cart-drawer-upsell-variants');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  }

  function upsellGetSelection(card) {
    const sel = {};
    card.querySelectorAll('.cart-drawer-upsell-option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);
      const active = group.querySelector('.cart-drawer-upsell-option-btn.active');
      sel[pos] = active ? active.dataset.optionValue : null;
    });
    return sel;
  }

  function upsellGetSelectedVariant(card) {
    const variants = upsellGetVariants(card);
    const groups = card.querySelectorAll('.cart-drawer-upsell-option-group');
    if (groups.length === 0) return variants.find(v => v.available) || variants[0] || null;
    const sel = upsellGetSelection(card);
    const positions = Object.keys(sel).map(Number).sort((a, b) => a - b);
    if (positions.some(p => sel[p] === null)) return null;
    return variants.find(v => positions.every(p => v.options[p - 1] === sel[p])) || null;
  }

  // Disable option values with no available variant given the other group's
  // current selection; deselect anything that became invalid; toggle confirm.
  function upsellRefreshAvailability(card) {
    const variants = upsellGetVariants(card);
    const sel = upsellGetSelection(card);
    card.querySelectorAll('.cart-drawer-upsell-option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);
      group.querySelectorAll('.cart-drawer-upsell-option-btn').forEach(btn => {
        const val = btn.dataset.optionValue;
        const exists = variants.some(v => {
          if (!v.available) return false;
          if (v.options[pos - 1] !== val) return false;
          for (const p in sel) {
            const pp = parseInt(p, 10);
            if (pp === pos || sel[pp] === null) continue;
            if (v.options[pp - 1] !== sel[pp]) return false;
          }
          return true;
        });
        btn.disabled = !exists;
        btn.classList.toggle('cart-drawer-upsell-option-btn--unavailable', !exists);
      });
    });

    let changed = false;
    card.querySelectorAll('.cart-drawer-upsell-option-group').forEach(group => {
      const active = group.querySelector('.cart-drawer-upsell-option-btn.active');
      if (active && active.disabled) { active.classList.remove('active'); changed = true; }
    });
    if (changed) return upsellRefreshAvailability(card);

    const confirm = card.querySelector('.cart-drawer-upsell-confirm');
    if (confirm) {
      const variant = upsellGetSelectedVariant(card);
      confirm.disabled = !(variant && variant.available);
    }
  }

  function upsellPostAdd(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    }).then(response => {
      if (!response.ok) {
        return response.json().then(err => { throw new Error(err.description || 'Could not add item'); });
      }
      return response.json();
    });
  }

  function handleUpsellAdd(e) {
    // 1) Size pill selection
    const optBtn = e.target.closest('.cart-drawer-upsell-option-btn');
    if (optBtn) {
      if (optBtn.disabled) return;
      optBtn.closest('.cart-drawer-upsell-option-buttons')
        .querySelectorAll('.cart-drawer-upsell-option-btn')
        .forEach(b => b.classList.remove('active'));
      optBtn.classList.add('active');
      upsellRefreshAvailability(optBtn.closest('.cart-drawer-upsell-item'));
      return;
    }

    // 2) Confirm — add the exact selected variant
    const confirmBtn = e.target.closest('.cart-drawer-upsell-confirm');
    if (confirmBtn) {
      const card = confirmBtn.closest('.cart-drawer-upsell-item');
      const variant = upsellGetSelectedVariant(card);
      if (!variant || !variant.available) { upsellRefreshAvailability(card); return; }
      const original = confirmBtn.textContent;
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Adding...';
      upsellPostAdd(variant.id)
        .then(() => {
          updateDrawerContents();
          // Reset card back to collapsed state.
          const panel = card.querySelector('.cart-drawer-upsell-options');
          if (panel) panel.hidden = true;
          card.querySelectorAll('.cart-drawer-upsell-option-btn.active')
            .forEach(b => b.classList.remove('active'));
        })
        .catch(err => {
          console.error('Cart Drawer: Upsell add error', err);
          alert(err.message || 'Could not add item. Please try again.');
        })
        .finally(() => {
          confirmBtn.textContent = original;
          confirmBtn.disabled = false;
        });
      return;
    }

    // 3) "+" button
    const btn = e.target.closest('.cart-drawer-upsell-add');
    if (!btn) return;
    const card = btn.closest('.cart-drawer-upsell-item');

    // Multi-variant: reveal size selectors instead of adding.
    if (card && card.dataset.hasOptions === 'true') {
      const panel = card.querySelector('.cart-drawer-upsell-options');
      if (panel) {
        const opening = panel.hidden;
        panel.hidden = !panel.hidden;
        btn.classList.toggle('is-active', opening);
        if (opening) upsellRefreshAvailability(card);
        return;
      }
    }

    // Single-variant: add immediately.
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    console.log('Cart Drawer: Adding upsell item', variantId);
    btn.disabled = true;
    const originalHTML = btn.innerHTML;
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="32"><animate attributeName="stroke-dashoffset" values="32;0" dur="0.8s" repeatCount="indefinite"/></circle></svg>';

    upsellPostAdd(variantId)
      .then(item => {
        console.log('Cart Drawer: Upsell item added', item);
        updateDrawerContents();
      })
      .catch(err => {
        console.error('Cart Drawer: Upsell add error', err);
        alert(err.message || 'Could not add item. Please try again.');
      })
      .finally(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
  }

  function openDrawer() {
    console.log('Cart Drawer: Opening...');

    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-drawer-overlay');

    if (drawer) drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');

    // Non-modal: do NOT scroll-lock the page. The drawer is a non-blocking
    // right-side overlay panel; the rest of the page stays scrollable/clickable.
    startPageScrollLockGuard();
  }

  function closeDrawer() {
    console.log('Cart Drawer: Closing...');

    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-drawer-overlay');

    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');

    // Nothing to restore - body scroll is never locked (non-modal drawer).
    stopPageScrollLockGuard();
  }

  function handleCloseClick(e) {
    if (e.target.closest('.cart-drawer-close')) {
      e.preventDefault();
      closeDrawer();
    }
    if (e.target.classList.contains('cart-drawer-overlay')) {
      closeDrawer();
    }
    if (e.target.closest('.cart-drawer-continue')) {
      e.preventDefault();
      closeDrawer();
    }
  }

  function handleEscape(e) {
    if (e.key === 'Escape') {
      const drawer = document.querySelector('.cart-drawer');
      if (drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    }
  }

  // Expose globally for debugging and external use
  window.CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    update: updateDrawerContents,
    addVariant: function(variantId, quantity) {
      quantity = quantity || 1;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId), quantity: quantity })
      })
      .then(r => r.json())
      .then(() => {
        updateDrawerContents();
        openDrawer();
      });
    }
  };

})();
